import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaWhatsapp,
  FaPhone,
  FaEnvelope,
  FaShareAlt,
  FaRegCopy,
  FaEdit,
  FaEye,
  FaQrcode,
  FaTrash,
  FaPlus,
  FaEllipsisV,
  FaChartLine,
  FaFolderOpen,
  FaBriefcase,
} from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../components/TablePagination";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";
import { SupportSortableHeader } from "../pages/SupportSortableHeader";
import { generateMobileDialQrDataUrl, MobileDialQrModal } from "./MobileDialQr";
import PortalStatusSelect, { getLeadStatusTone } from "./PortalStatusSelect";
import { canDeleteInPortal, getPortalBusiness } from "./portalSession";
import { leadFieldProfile } from "../utils/leadProfile";
import PortalAlert from "./PortalAlert";
import PortalLeadQuickFollowupModal from "./PortalLeadQuickFollowupModal";
import PortalConfirmModal from "./PortalConfirmModal";
import LeadImportModal from "./LeadImportModal";
import PortalResultsHead from "./PortalResultsHead";
import { formatBusinessName, formatLocation, formatPhone } from "../utils/formatters";
import PortalFiltersCollapsible, { usePortalCompactFilters } from "./PortalFiltersCollapsible";
import "../assets/css/DigixLeads.css";

const PORTAL_LEADS_ROWS_PER_PAGE_KEY = "portalLeadsRowsPerPage";
const PORTAL_LEADS_CURRENT_PAGE_KEY = "portalLeadsCurrentPage";

function toPascal(value) {
  const s = String(value || "").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}

function normalizedCountryCode(l) {
  const raw = String(l.Country_Code || "").trim();
  if (!raw) return "";
  return raw.startsWith("+") ? raw : `+${raw}`;
}

function buildShare(l) {
  const shareName =
    [toPascal(l.First_Name), toPascal(l.Last_Name)].filter(Boolean).join(" ") ||
    `#${l.Real_Estate_Lead_Id}`;
  const cc = normalizedCountryCode(l);
  const title = `Real Estate Lead - ${shareName}`;
  const text = [
    title,
    l.Mobile_Number ? `Contact: ${[cc, l.Mobile_Number].filter(Boolean).join(" ")}` : "",
    l.Email ? `Email: ${l.Email}` : "",
    l.Property_Service ? `Service: ${l.Property_Service}` : "",
    l.Tenant_Project_Name ? `Project: ${l.Tenant_Project_Name}` : "",
    l.Property_Type_Name ? `Property Type: ${l.Property_Type_Name}` : "",
    l.Requirement_Type ? `Requirement Type: ${l.Requirement_Type}` : "",
    l.Sharing_Preference ? `Sharing Preference: ${l.Sharing_Preference}` : "",
    l.Move_In_Date ? `Move-in Date: ${formatDate(l.Move_In_Date)}` : "",
    l.Preferred_Location ? `Lead Location: ${l.Preferred_Location}` : "",
  ]
    .filter(Boolean)
    .join("\n");
  return { title, text };
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function fullName(l) {
  return `${(l.First_Name || "").trim()} ${(l.Last_Name || "").trim()}`.trim() || "—";
}

function phone(l) {
  if (!l.Mobile_Number) return "—";
  const digits = `${l.Country_Code || ""}${l.Mobile_Number}`.replace(/\D/g, "");
  return formatPhone(digits) || "—";
}

// Value accessors used for sorting each column.
const SORT_ACCESSORS = {
  fullName: (l) => fullName(l).toLowerCase(),
  contact: (l) => l.Mobile_Number || "",
  status: (l) => (l.Lead_Status || "").toLowerCase(),
  nextFollowup: (l) =>
    l.Last_Next_Followup_Date ? new Date(l.Last_Next_Followup_Date).getTime() || 0 : 0,
  followupNotes: (l) => (l.Last_Followup_Notes || "").toLowerCase(),
  project: (l) => (l.Tenant_Project_Name || "").toLowerCase(),
  businessType: (l) => (l.Domain_Name || "").toLowerCase(),
  propertyService: (l) => (l.Property_Service || "").toLowerCase(),
  propertyLocation: (l) => (l.Property_Location || "").toLowerCase(),
  propertyType: (l) => (l.Property_Type_Name || "").toLowerCase(),
  email: (l) => (l.Email || "").toLowerCase(),
  location: (l) => (l.Preferred_Location || "").toLowerCase(),
  created: (l) => (l.Created_On ? new Date(l.Created_On).getTime() || 0 : 0),
};

const COLUMNS = [
  { key: "fullName", label: "Full Name" },
  { key: "contact", label: "Contact" },
  { key: "status", label: "Status" },
  { key: "nextFollowup", label: "Next Follow-up" },
  { key: "followupNotes", label: "Follow-up Notes" },
  { key: "project", label: "Project" },
  { key: "businessType", label: "Business Type" },
  { key: "propertyService", label: "Service" },
  { key: "propertyLocation", label: "Property Location" },
  { key: "propertyType", label: "Property Type" },
  { key: "email", label: "Email" },
  { key: "location", label: "Lead Loc" },
];

const DATE_PRESETS = [
  { id: "all", label: "All time" },
  { id: "today", label: "Today" },
  { id: "7", label: "Last 7 days" },
  { id: "30", label: "Last 30 days" },
  { id: "month", label: "This month" },
  { id: "custom", label: "Custom" },
];

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function presetRange(preset, fromDate, toDate) {
  const now = new Date();
  switch (preset) {
    case "today":
      return { from: startOfDay(now), to: endOfDay(now) };
    case "7": {
      const from = startOfDay(now);
      from.setDate(from.getDate() - 6);
      return { from, to: endOfDay(now) };
    }
    case "30": {
      const from = startOfDay(now);
      from.setDate(from.getDate() - 29);
      return { from, to: endOfDay(now) };
    }
    case "month":
      return { from: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), to: endOfDay(now) };
    case "custom":
      return {
        from: fromDate ? startOfDay(new Date(fromDate)) : null,
        to: toDate ? endOfDay(new Date(toDate)) : null,
      };
    default:
      return { from: null, to: null };
  }
}

function toNumber(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/[^\d.-]/g, ""));
  return Number.isNaN(n) ? null : n;
}

function TenantLeads({ mode = "leads" }) {
  const navigate = useNavigate();
  const heading = mode === "customers" ? "Customers" : mode === "clients" ? "Clients" : "Leads";
  const noun = mode === "customers" ? "customer" : mode === "clients" ? "client" : "lead";
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [search, setSearch] = useState("");
  const [resultKeyword, setResultKeyword] = useState("");
  const [datePreset, setDatePreset] = useState("7");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [businessTypeFilter, setBusinessTypeFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [minBudget, setMinBudget] = useState("");
  const [maxBudget, setMaxBudget] = useState("");
  const [sortKey, setSortKey] = useState("created");
  const [sortDir, setSortDir] = useState("desc");
  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const saved = localStorage.getItem(PORTAL_LEADS_CURRENT_PAGE_KEY);
      if (!saved) return 1;
      const parsed = parseInt(saved, 10);
      return parsed > 0 ? parsed : 1;
    } catch {
      return 1;
    }
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(PORTAL_LEADS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) return parsed;
    } catch {
      /* ignore */
    }
    return 10;
  });
  const [shareModal, setShareModal] = useState({ open: false, title: "", text: "" });
  const [qrModal, setQrModal] = useState({ open: false, dataUrl: "", title: "", number: "" });
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [tenantBusiness, setTenantBusiness] = useState(() => getPortalBusiness());
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [statusMessage, setStatusMessage] = useState({ type: "", text: "" });
  const [deletingId, setDeletingId] = useState(null);
  const [deleteLead, setDeleteLead] = useState(null);
  const [followupLead, setFollowupLead] = useState(null);
  const [notesLead, setNotesLead] = useState(null);
  const [openContactId, setOpenContactId] = useState(null);
  const [contactMenuPos, setContactMenuPos] = useState(null);
  const contactMenuRef = useRef(null);
  const canDelete = useMemo(() => canDeleteInPortal(), []);
  const { filtersOpen, setFiltersOpen, isCompactViewport } = usePortalCompactFilters();

  const closeContactMenu = () => {
    setOpenContactId(null);
    setContactMenuPos(null);
  };

  const toggleContactMenu = (leadId, event) => {
    if (openContactId === leadId) {
      closeContactMenu();
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const menuApproxHeight = 170;
    const openUp = rect.bottom + menuApproxHeight + 8 > window.innerHeight;
    setContactMenuPos({
      top: openUp ? undefined : rect.bottom + 4,
      bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 240)),
    });
    setOpenContactId(leadId);
  };

  useEffect(() => {
    if (openContactId == null) return undefined;
    const onPointerDown = (event) => {
      if (contactMenuRef.current && !contactMenuRef.current.contains(event.target)) {
        closeContactMenu();
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeContactMenu();
    };
    const onRepositionClose = () => closeContactMenu();
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onRepositionClose);
    window.addEventListener("scroll", onRepositionClose, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onRepositionClose);
      window.removeEventListener("scroll", onRepositionClose, true);
    };
  }, [openContactId]);

  const loadLeads = async () => {
    const leadsRes = await axiosInstance.get("/client/leads");
    setLeads(Array.isArray(leadsRes.data?.data) ? leadsRes.data.data : []);
  };

  const openFollowupModal = (lead) => setFollowupLead(lead);
  const closeFollowupModal = () => setFollowupLead(null);

  const handleFollowupSaved = async () => {
    await loadLeads();
    setStatusMessage({ type: "success", text: "Follow-up updated." });
  };

  const openShare = (l) => {
    const { title, text } = buildShare(l);
    setShareModal({ open: true, title, text });
  };
  const closeShare = () => setShareModal({ open: false, title: "", text: "" });

  const openMobileQr = async (lead) => {
    if (!lead?.Mobile_Number) return;
    try {
      const dataUrl = await generateMobileDialQrDataUrl(lead.Country_Code, lead.Mobile_Number);
      if (!dataUrl) return;
      setQrModal({
        open: true,
        dataUrl,
        title: fullName(lead),
        number: phone(lead),
      });
    } catch (err) {
      console.error("Error generating mobile QR code:", err);
      alert("Unable to generate QR code for this number.");
    }
  };
  const closeMobileQr = () => setQrModal({ open: false, dataUrl: "", title: "", number: "" });

  const copyShareText = async () => {
    const text = shareModal.text;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      alert("Lead details copied to clipboard.");
    } catch (_) {
      alert("Unable to copy details.");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({ title: shareModal.title, text: shareModal.text });
      closeShare();
    } catch (_) {
      /* user cancelled */
    }
  };

  const toggleSort = (key) => {
    setCurrentPage(1);
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setCurrentPage(1);
    localStorage.setItem(PORTAL_LEADS_ROWS_PER_PAGE_KEY, String(next));
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [leadsRes, metaRes] = await Promise.all([
          axiosInstance.get("/client/leads"),
          axiosInstance.get("/client/lead-meta").catch(() => ({ data: { data: {} } })),
        ]);
        if (!active) return;
        setLeads(Array.isArray(leadsRes.data?.data) ? leadsRes.data.data : []);
        const meta = metaRes.data?.data || {};
        setLeadStatuses(Array.isArray(meta.leadStatuses) ? meta.leadStatuses : []);
        setLeadSources(Array.isArray(meta.sources) ? meta.sources : []);
        setTenantBusiness((prev) => ({
          type: meta.businessType || prev.type,
          model: meta.businessModel || prev.model,
          allBusiness:
            typeof meta.capturesAllBusinessLeads === "boolean"
              ? meta.capturesAllBusinessLeads
              : prev.allBusiness,
        }));
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.message || "Could not load your leads.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [reloadKey]);

  const updateLeadStatus = async (lead, statusId) => {
    const nextId = statusId ? Number(statusId) : null;
    if (!lead?.Real_Estate_Lead_Id || !nextId || Number(lead.Lead_Status_Id) === nextId) return;

    const previous = lead;
    const selected = leadStatuses.find((s) => Number(s.Status_Id) === nextId);
    setStatusUpdatingId(lead.Real_Estate_Lead_Id);
    setStatusMessage({ type: "", text: "" });
    setLeads((rows) =>
      rows.map((row) =>
        row.Real_Estate_Lead_Id === lead.Real_Estate_Lead_Id
          ? {
              ...row,
              Lead_Status_Id: nextId,
              Lead_Status: selected?.Status || row.Lead_Status,
            }
          : row
      )
    );

    try {
      const res = await axiosInstance.put(`/client/leads/${lead.Real_Estate_Lead_Id}`, {
        Lead_Status_Id: nextId,
      });
      const updated = res.data?.data;
      if (updated) {
        setLeads((rows) =>
          rows.map((row) =>
            row.Real_Estate_Lead_Id === lead.Real_Estate_Lead_Id ? { ...row, ...updated } : row
          )
        );
      }
      setStatusMessage({ type: "success", text: "Status updated." });
    } catch (err) {
      setLeads((rows) =>
        rows.map((row) =>
          row.Real_Estate_Lead_Id === lead.Real_Estate_Lead_Id ? previous : row
        )
      );
      setStatusMessage({
        type: "error",
        text: err.response?.data?.message || "Could not update status.",
      });
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const requestDeleteLead = (lead) => {
    if (!canDelete || !lead?.Real_Estate_Lead_Id) return;
    setDeleteLead(lead);
  };

  const closeDeleteModal = () => {
    if (deletingId) return;
    setDeleteLead(null);
  };

  const confirmDeleteLead = async () => {
    const lead = deleteLead;
    if (!canDelete || !lead?.Real_Estate_Lead_Id) return;

    setDeletingId(lead.Real_Estate_Lead_Id);
    setError("");
    setStatusMessage({ type: "", text: "" });
    try {
      await axiosInstance.delete(`/client/leads/${lead.Real_Estate_Lead_Id}`);
      setLeads((rows) => rows.filter((row) => row.Real_Estate_Lead_Id !== lead.Real_Estate_Lead_Id));
      setStatusMessage({ type: "success", text: "Lead deleted." });
      setDeleteLead(null);
    } catch (err) {
      setError(err.response?.data?.message || "Could not delete lead.");
    } finally {
      setDeletingId(null);
    }
  };

  const statusOptions = useMemo(() => {
    const fromMeta = leadStatuses.map((s) => s.Status).filter(Boolean);
    const fromLeads = leads.map((l) => l.Lead_Status).filter(Boolean);
    return Array.from(new Set([...fromMeta, ...fromLeads])).sort((a, b) =>
      String(a).localeCompare(String(b))
    );
  }, [leadStatuses, leads]);

  const sourceOptions = useMemo(() => {
    const fromMeta = leadSources.map((s) => s.Source_Name).filter(Boolean);
    const fromLeads = leads.map((l) => l.Source_Name).filter(Boolean);
    return Array.from(new Set([...fromMeta, ...fromLeads])).sort((a, b) =>
      String(a).localeCompare(String(b))
    );
  }, [leadSources, leads]);
  const locationOptions = useMemo(
    () => Array.from(new Set(leads.map((l) => l.Preferred_Location).filter(Boolean))).sort(),
    [leads]
  );

  // Service-provider tenants hold leads from many business types, so their filters follow the data.
  const leadProfile = leadFieldProfile(tenantBusiness.type, tenantBusiness.model);
  const hasBudgetData = useMemo(
    () => leads.some((l) => toNumber(l.Budget_Min) !== null || toNumber(l.Budget_Max) !== null),
    [leads]
  );
  const showBudgetFilter = tenantBusiness.allBusiness ? hasBudgetData : leadProfile.showBudget;

  const showProjectCol = useMemo(
    () => leads.some((l) => String(l.Tenant_Project_Name || "").trim()),
    [leads]
  );
  const businessTypeOptions = useMemo(
    () => Array.from(new Set(leads.map((l) => l.Domain_Name).filter(Boolean))).sort(),
    [leads]
  );
  const showBusinessTypeCol = businessTypeOptions.length > 0;
  const showServiceCol = useMemo(
    () =>
      leads.some(
        (l) =>
          String(l.Property_Service || "").trim() || String(l.Property_Service_Link || "").trim()
      ),
    [leads]
  );
  const showPropertyLocationCol = useMemo(
    () => leads.some((l) => String(l.Property_Location || "").trim()),
    [leads]
  );
  const showPropertyTypeCol = useMemo(
    () => leads.some((l) => String(l.Property_Type_Name || "").trim()),
    [leads]
  );
  const visibleColumns = useMemo(
    () =>
      COLUMNS.filter((col) => {
        if (col.key === "project") return showProjectCol;
        if (col.key === "businessType") return showBusinessTypeCol;
        if (col.key === "propertyService") return showServiceCol;
        if (col.key === "propertyLocation") return showPropertyLocationCol;
        if (col.key === "propertyType") return showPropertyTypeCol;
        return true;
      }),
    [showProjectCol, showBusinessTypeCol, showServiceCol, showPropertyLocationCol, showPropertyTypeCol]
  );

  const filtered = useMemo(() => {
    let list = leads;

    const { from, to } = presetRange(datePreset, fromDate, toDate);
    if (from || to) {
      list = list.filter((l) => {
        if (!l.Created_On) return false;
        const created = new Date(l.Created_On);
        if (Number.isNaN(created.getTime())) return false;
        if (from && created < from) return false;
        if (to && created > to) return false;
        return true;
      });
    }

    if (statusFilter) list = list.filter((l) => l.Lead_Status === statusFilter);
    if (sourceFilter) list = list.filter((l) => l.Source_Name === sourceFilter);
    if (businessTypeFilter) list = list.filter((l) => l.Domain_Name === businessTypeFilter);
    if (locationFilter) list = list.filter((l) => l.Preferred_Location === locationFilter);

    const min = showBudgetFilter ? toNumber(minBudget) : null;
    const max = showBudgetFilter ? toNumber(maxBudget) : null;
    if (min !== null) {
      list = list.filter((l) => {
        const v = toNumber(l.Budget_Max) ?? toNumber(l.Budget_Min);
        return v !== null && v >= min;
      });
    }
    if (max !== null) {
      list = list.filter((l) => {
        const v = toNumber(l.Budget_Min) ?? toNumber(l.Budget_Max);
        return v !== null && v <= max;
      });
    }

    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((l) =>
      [
        l.First_Name,
        l.Last_Name,
        l.Mobile_Number,
        l.Email,
        l.Preferred_Location,
        l.Tenant_Project_Name,
        l.Property_Service,
        l.Property_Location,
        l.Property_Type_Name,
        l.Requirement_Type,
        l.Sharing_Preference,
        l.Move_In_Date,
        l.Lead_Status,
        l.Last_Followup_Notes,
        l.Last_Next_Followup_Date,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [
    leads,
    search,
    datePreset,
    fromDate,
    toDate,
    statusFilter,
    sourceFilter,
    businessTypeFilter,
    locationFilter,
    minBudget,
    maxBudget,
    showBudgetFilter,
  ]);

  const resultFiltered = useMemo(() => {
    const q = resultKeyword.trim().toLowerCase();
    if (!q) return filtered;
    return filtered.filter((l) =>
      [
        l.First_Name,
        l.Last_Name,
        l.Mobile_Number,
        l.Email,
        l.Lead_Status,
        l.Last_Followup_Notes,
        l.Tenant_Project_Name,
        l.Domain_Name,
        l.Property_Service,
        l.Property_Location,
        l.Property_Type_Name,
        l.Preferred_Location,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [filtered, resultKeyword]);

  const hasActiveFilters =
    datePreset !== "7" ||
    !!statusFilter ||
    !!sourceFilter ||
    !!businessTypeFilter ||
    !!locationFilter ||
    !!minBudget ||
    !!maxBudget ||
    !!search;

  const clearFilters = () => {
    setDatePreset("7");
    setFromDate("");
    setToDate("");
    setStatusFilter("");
    setSourceFilter("");
    setBusinessTypeFilter("");
    setLocationFilter("");
    setMinBudget("");
    setMaxBudget("");
    setSearch("");
    setCurrentPage(1);
  };

  const sorted = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortKey];
    if (!accessor) return resultFiltered;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...resultFiltered].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [resultFiltered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const pageRows = sorted.slice(startIndex, startIndex + rowsPerPage);

  useEffect(() => {
    if (currentPage > 0) {
      localStorage.setItem(PORTAL_LEADS_CURRENT_PAGE_KEY, String(currentPage));
    }
  }, [currentPage]);

  const skipFilterPageResetRef = React.useRef(true);
  useEffect(() => {
    if (skipFilterPageResetRef.current) {
      skipFilterPageResetRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [search, resultKeyword, datePreset, fromDate, toDate, statusFilter, sourceFilter, businessTypeFilter, locationFilter, minBudget, maxBudget]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  return (
    <div className="cportal-page cportal-page--leads">
      <div className="cportal-page-head">
        <h1>{heading}</h1>
      </div>

      <PortalAlert type="error" message={error} onClear={() => setError("")} />
      <PortalAlert
        type={statusMessage.type === "error" ? "error" : "success"}
        message={statusMessage.text}
        onClear={() => setStatusMessage({ type: "", text: "" })}
      />

      {mode !== "clients" && (
      <PortalFiltersCollapsible
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search name, phone, email"
        searchAriaLabel="Search leads"
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        isCompactViewport={isCompactViewport}
        hasActiveFilters={hasActiveFilters}
        onClear={clearFilters}
        actions={
          mode === "leads" ? (
            <>
              <button type="button" className="cportal-btn cportal-btn-ghost" onClick={() => setImportOpen(true)}>
                Import
              </button>
              <button
                type="button"
                className="cportal-btn cportal-btn-primary"
                onClick={() => navigate("/portal/leads/new")}
              >
                Add Lead
              </button>
            </>
          ) : null
        }
      >
        <div className="cportal-filter-presets">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`cportal-chip ${datePreset === p.id ? "active" : ""}`}
              onClick={() => setDatePreset(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>

        {datePreset === "custom" && (
          <div className="cportal-filter-dates">
            <label className="cportal-field">
              <span>From date</span>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </label>
            <label className="cportal-field">
              <span>To date</span>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </label>
          </div>
        )}

        <div
          className={`cportal-filter-row cportal-lead-filter-grid${
            businessTypeOptions.length ? "" : " cportal-lead-filter-grid--no-type"
          }`}
        >
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
            <option value="">All Sources</option>
            {sourceOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {businessTypeOptions.length ? (
            <select value={businessTypeFilter} onChange={(e) => setBusinessTypeFilter(e.target.value)}>
              <option value="">All Business Types</option>
              {businessTypeOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          ) : null}
          <select
            className="cportal-filter-location"
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
          >
            <option value="">All Locations</option>
            {locationOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {showBudgetFilter ? (
            <>
              <input
                className="cportal-budget"
                type="number"
                placeholder="Min Budget"
                value={minBudget}
                onChange={(e) => setMinBudget(e.target.value)}
              />
              <input
                className="cportal-budget"
                type="number"
                placeholder="Max Budget"
                value={maxBudget}
                onChange={(e) => setMaxBudget(e.target.value)}
              />
            </>
          ) : null}
        </div>
      </PortalFiltersCollapsible>
      )}

      <div className="cportal-card">
        <PortalResultsHead
          heading={heading}
          count={sorted.length}
          keyword={resultKeyword}
          onKeywordChange={setResultKeyword}
          ariaLabel={`Keyword search ${noun}s`}
        />

        {loading ? (
          <p className="cportal-muted">Loading…</p>
        ) : sorted.length === 0 ? (
          <p className="cportal-muted">No {noun}s found.</p>
        ) : (
          <>
          <div className="cportal-table-wrap co-desktop-table">
            <table className="cportal-table">
              <thead>
                <tr>
                  {visibleColumns.map((col) => (
                    <th key={col.key} className="cportal-th-sortable">
                      <SupportSortableHeader
                        columnId={col.key}
                        label={col.label}
                        sortColumn={sortKey}
                        sortDirection={sortDir}
                        onSort={toggleSort}
                      />
                    </th>
                  ))}
                  <th className="cportal-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((l) => (
                  <tr key={l.Real_Estate_Lead_Id}>
                    <td>
                      <button
                        type="button"
                        className="cportal-table-link"
                        onClick={() =>
                          navigate(`/portal/leads/${l.Real_Estate_Lead_Id}`, { state: { from: mode } })
                        }
                      >
                        {fullName(l)}
                      </button>
                    </td>
                    <td>
                      <div className="cportal-contact-cell">
                        <div className="cportal-contact-primary">
                          <span>{phone(l)}</span>
                        </div>
                        {(l.Mobile_Number || l.Email) && (
                          <div
                            className={`cportal-actions-menu cportal-contact-menu${
                              openContactId === l.Real_Estate_Lead_Id ? " is-open" : ""
                            }`}
                            ref={openContactId === l.Real_Estate_Lead_Id ? contactMenuRef : null}
                          >
                            <button
                              type="button"
                              className="cportal-actions-menu-trigger"
                              aria-label="Open contact actions"
                              aria-haspopup="menu"
                              aria-expanded={openContactId === l.Real_Estate_Lead_Id}
                              onClick={(event) => toggleContactMenu(l.Real_Estate_Lead_Id, event)}
                            >
                              <FaEllipsisV aria-hidden="true" />
                            </button>
                            {openContactId === l.Real_Estate_Lead_Id && contactMenuPos && (
                              <div
                                className="cportal-actions-menu-panel cportal-contact-menu-panel"
                                role="menu"
                                style={{
                                  top: contactMenuPos.top,
                                  bottom: contactMenuPos.bottom,
                                  left: contactMenuPos.left,
                                }}
                              >
                                {l.Mobile_Number && (
                                  <>
                                    <a
                                      className="cportal-contact-menu-item"
                                      role="menuitem"
                                      href={`tel:${normalizedCountryCode(l)}${l.Mobile_Number}`}
                                      onClick={closeContactMenu}
                                    >
                                      <FaPhone style={{ transform: "scaleX(-1)" }} aria-hidden="true" />
                                      <span>
                                        <strong>Call</strong>
                                        <em>{phone(l)}</em>
                                      </span>
                                    </a>
                                    <a
                                      className="cportal-contact-menu-item cportal-contact-menu-item--whatsapp"
                                      role="menuitem"
                                      href={`https://wa.me/${String(l.Country_Code || "").replace(/\D/g, "")}${String(l.Mobile_Number).replace(/\D/g, "")}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={closeContactMenu}
                                    >
                                      <FaWhatsapp aria-hidden="true" />
                                      <span>
                                        <strong>WhatsApp</strong>
                                        <em>{phone(l)}</em>
                                      </span>
                                    </a>
                                    <button
                                      type="button"
                                      className="cportal-contact-menu-item"
                                      role="menuitem"
                                      onClick={() => {
                                        closeContactMenu();
                                        openMobileQr(l);
                                      }}
                                    >
                                      <FaQrcode aria-hidden="true" />
                                      <span>
                                        <strong>Dial QR</strong>
                                        <em>{phone(l)}</em>
                                      </span>
                                    </button>
                                  </>
                                )}
                                {l.Email && (
                                  <a
                                    className="cportal-contact-menu-item cportal-contact-menu-item--email"
                                    role="menuitem"
                                    href={`mailto:${l.Email}`}
                                    onClick={closeContactMenu}
                                  >
                                    <FaEnvelope aria-hidden="true" />
                                    <span>
                                      <strong>Email</strong>
                                      <em>{l.Email}</em>
                                    </span>
                                  </a>
                                )}
                                <button
                                  type="button"
                                  className="cportal-contact-menu-item cportal-contact-menu-item--share"
                                  role="menuitem"
                                  onClick={() => {
                                    closeContactMenu();
                                    openShare(l);
                                  }}
                                >
                                  <FaShareAlt aria-hidden="true" />
                                  <span>
                                    <strong>Share</strong>
                                    <em>Share lead details</em>
                                  </span>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      {leadStatuses.length > 0 ? (
                        <PortalStatusSelect
                          value={l.Lead_Status_Id}
                          label={l.Lead_Status}
                          options={leadStatuses}
                          disabled={statusUpdatingId === l.Real_Estate_Lead_Id}
                          onChange={(statusId) => updateLeadStatus(l, statusId)}
                          ariaLabel={`Change status for ${fullName(l)}`}
                        />
                      ) : (
                        <span
                          className={
                            getLeadStatusTone(l.Lead_Status) === "neutral"
                              ? "cportal-badge"
                              : `cportal-badge cportal-badge--${getLeadStatusTone(l.Lead_Status)}`
                          }
                        >
                          {l.Lead_Status || "—"}
                        </span>
                      )}
                    </td>
                    <td className={!l.Last_Next_Followup_Date ? "muted-text" : ""}>
                      {formatDate(l.Last_Next_Followup_Date)}
                    </td>
                    <td className={`cportal-td-notes${!l.Last_Followup_Notes ? " muted-text" : ""}`}>
                      {l.Last_Followup_Notes ? (
                        <button
                          type="button"
                          className="cportal-notes-trigger"
                          onClick={() => setNotesLead(l)}
                          title="View full notes"
                        >
                          {l.Last_Followup_Notes}
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                    {showProjectCol ? <td>{l.Tenant_Project_Name || "—"}</td> : null}
                    {showBusinessTypeCol ? <td>{l.Domain_Name || "—"}</td> : null}
                    {showServiceCol ? (
                      <td>
                        {l.Property_Service_Link ? (
                          <a
                            href={l.Property_Service_Link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cportal-table-link"
                            title="Open service link"
                          >
                            {l.Property_Service || "View link"}
                          </a>
                        ) : (
                          l.Property_Service || "—"
                        )}
                      </td>
                    ) : null}
                    {showPropertyLocationCol ? <td>{formatLocation(l.Property_Location) || "—"}</td> : null}
                    {showPropertyTypeCol ? <td>{l.Property_Type_Name || "—"}</td> : null}
                    <td>{l.Email || "—"}</td>
                    <td>{formatLocation(l.Preferred_Location) || "—"}</td>
                    <td className="cportal-td-actions">
                      <div className="cportal-re-actions">
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--followup"
                          onClick={() => openFollowupModal(l)}
                          title="Update follow-up"
                          aria-label="Update follow-up"
                        >
                          <FaPlus size={16} />
                        </button>
                        {(() => {
                          const waDigits = `${String(l.Country_Code || "").replace(/\D/g, "")}${String(
                            l.Mobile_Number || ""
                          ).replace(/\D/g, "")}`;
                          const waHref = waDigits.length >= 10 ? `https://wa.me/${waDigits}` : null;
                          const telHref = l.Mobile_Number
                            ? `tel:${normalizedCountryCode(l)}${l.Mobile_Number}`
                            : null;
                          return (
                            <>
                              {waHref ? (
                                <a
                                  className="re-lead-action re-lead-action--whatsapp"
                                  href={waHref}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={`WhatsApp ${phone(l)}`}
                                  aria-label="Open WhatsApp"
                                >
                                  <FaWhatsapp size={15} />
                                </a>
                              ) : (
                                <span
                                  className="re-lead-action re-lead-action--whatsapp re-lead-action--disabled"
                                  title="WhatsApp unavailable"
                                  aria-disabled="true"
                                >
                                  <FaWhatsapp size={15} />
                                </span>
                              )}
                              {telHref ? (
                                <a
                                  className="re-lead-action re-lead-action--call"
                                  href={telHref}
                                  title={`Call ${phone(l)}`}
                                  aria-label="Call lead"
                                >
                                  <FaPhone size={12} style={{ transform: "scaleX(-1)" }} />
                                </a>
                              ) : (
                                <span
                                  className="re-lead-action re-lead-action--call re-lead-action--disabled"
                                  title="Call unavailable"
                                  aria-disabled="true"
                                >
                                  <FaPhone size={12} style={{ transform: "scaleX(-1)" }} />
                                </span>
                              )}
                            </>
                          );
                        })()}
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--view"
                          onClick={() =>
                            navigate(`/portal/leads/${l.Real_Estate_Lead_Id}`, {
                              state: { from: mode },
                            })
                          }
                          title="View lead"
                          aria-label="View lead"
                        >
                          <FaEye size={16} />
                        </button>
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--edit"
                          onClick={() =>
                            navigate(`/portal/leads/${l.Real_Estate_Lead_Id}`, {
                              state: { edit: true, from: mode },
                            })
                          }
                          title="Edit lead"
                          aria-label="Edit lead"
                        >
                          <FaEdit size={16} />
                        </button>
                        {canDelete && (
                          <button
                            type="button"
                            className="re-lead-action re-lead-action--delete"
                            onClick={() => requestDeleteLead(l)}
                            disabled={deletingId === l.Real_Estate_Lead_Id}
                            title="Delete lead"
                            aria-label="Delete lead"
                          >
                            {deletingId === l.Real_Estate_Lead_Id ? "…" : <FaTrash size={16} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ResultsMobileList emptyLabel={`No ${noun}s found.`} className="cportal-leads-mobile">
            {pageRows.map((l) => {
              const leadId = l.Real_Estate_Lead_Id;
              const status = l.Lead_Status || "—";
              const statusTone = getLeadStatusTone(status) || "neutral";
              const cardTone =
                statusTone === "danger" ? "danger" : statusTone === "warning" ? "warning" : undefined;
              const telHref = l.Mobile_Number
                ? `tel:${normalizedCountryCode(l)}${l.Mobile_Number}`
                : null;
              const waDigits = `${String(l.Country_Code || "").replace(/\D/g, "")}${String(
                l.Mobile_Number || ""
              ).replace(/\D/g, "")}`;
              const waHref = waDigits.length >= 10 ? `https://wa.me/${waDigits}` : null;
              return (
                <ResultsMobileCard
                  key={leadId}
                  title={fullName(l)}
                  titleIcon={<FaChartLine />}
                  tone={cardTone}
                  meta={[
                    { label: status, tone: statusTone, status: true },
                    l.Tenant_Project_Name
                      ? {
                          label: formatBusinessName(l.Tenant_Project_Name),
                          tone: "info",
                          icon: <FaFolderOpen aria-hidden="true" />,
                        }
                      : l.Property_Service
                        ? {
                            label: formatBusinessName(l.Property_Service),
                            tone: "info",
                            icon: <FaBriefcase aria-hidden="true" />,
                          }
                        : null,
                  ].filter(Boolean)}
                  onOpen={() =>
                    navigate(`/portal/leads/${leadId}`, { state: { from: mode } })
                  }
                  actions={
                    <div className="co-mcard__action-grid">
                      {waHref ? (
                        <a
                          className="co-maction co-maction--whatsapp"
                          href={waHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="WhatsApp"
                          aria-label="WhatsApp"
                        >
                          <FaWhatsapp size={14} />
                          <span>Chat</span>
                        </a>
                      ) : null}
                      <button
                        type="button"
                        className="co-maction"
                        title="Follow-up"
                        aria-label="Follow-up"
                        onClick={() => openFollowupModal(l)}
                      >
                        <FaPlus size={14} />
                        <span>Follow</span>
                      </button>
                      <button
                        type="button"
                        className="co-maction"
                        title="Edit"
                        aria-label="Edit"
                        onClick={() =>
                          navigate(`/portal/leads/${leadId}`, {
                            state: { edit: true, from: mode },
                          })
                        }
                      >
                        <FaEdit size={14} />
                        <span>Edit</span>
                      </button>
                      <button
                        type="button"
                        className="co-maction"
                        title="Share"
                        aria-label="Share"
                        onClick={() => openShare(l)}
                      >
                        <FaShareAlt size={14} />
                        <span>Share</span>
                      </button>
                      {canDelete ? (
                        <button
                          type="button"
                          className="co-maction co-maction--danger"
                          title="Delete"
                          aria-label="Delete"
                          disabled={deletingId === leadId}
                          onClick={() => requestDeleteLead(l)}
                        >
                          <FaTrash size={14} />
                          <span>Delete</span>
                        </button>
                      ) : null}
                    </div>
                  }
                >
                  {(() => {
                    const phoneText = phone(l) !== "—" ? phone(l) : "";
                    const location = formatLocation(l.Preferred_Location || l.Property_Location || "");
                    const nextFollowup = l.Last_Next_Followup_Date ? formatDate(l.Last_Next_Followup_Date) : "";
                    const type = showPropertyTypeCol ? l.Property_Type_Name || "" : "";
                    const info = [
                      location ? { label: "Location", value: location } : null,
                      nextFollowup ? { label: "Next follow-up", value: nextFollowup } : null,
                      type ? { label: "Type", value: type } : null,
                    ].filter(Boolean);
                    return (
                      <>
                        {phoneText || l.Email ? (
                          <ul className="co-tcard__contact">
                            {phoneText ? (
                              <li>
                                <FaPhone aria-hidden="true" style={{ transform: "scaleX(-1)" }} />
                                {telHref ? (
                                  <a href={telHref} title={`Call ${phoneText}`}>
                                    {phoneText}
                                  </a>
                                ) : (
                                  <span>{phoneText}</span>
                                )}
                              </li>
                            ) : null}
                            {l.Email ? (
                              <li>
                                <FaEnvelope aria-hidden="true" />
                                <a href={`mailto:${l.Email}`} title={`Email ${l.Email}`}>
                                  {l.Email}
                                </a>
                              </li>
                            ) : null}
                          </ul>
                        ) : null}
                        {info.length || l.Last_Followup_Notes ? (
                          <dl className="co-tcard__info">
                            {info.map((item, i) => (
                              <div
                                key={item.label}
                                className={info.length % 2 === 1 && i === info.length - 1 ? "co-tcard__info-full" : undefined}
                              >
                                <dt>{item.label}</dt>
                                <dd>{item.value}</dd>
                              </div>
                            ))}
                            {l.Last_Followup_Notes ? (
                              <div className="co-tcard__info-full">
                                <dt>Notes</dt>
                                <dd>{l.Last_Followup_Notes}</dd>
                              </div>
                            ) : null}
                          </dl>
                        ) : null}
                      </>
                    );
                  })()}
                </ResultsMobileCard>
              );
            })}
          </ResultsMobileList>
          </>
        )}

        {sorted.length > 0 && !loading && (
          <TablePagination
            currentPage={currentPage}
            totalItems={sorted.length}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            itemLabel={`${noun}s`}
            rowsPerPageSelectId="portal-leads-rows-per-page"
          />
        )}
      </div>

      {notesLead && (
        <div className="cportal-modal-overlay" onClick={() => setNotesLead(null)}>
          <div className="cportal-modal cportal-notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cportal-modal-header">
              <h3>Follow-up Notes · {fullName(notesLead)}</h3>
              <button type="button" onClick={() => setNotesLead(null)}>
                Close
              </button>
            </div>
            <div className="cportal-notes-modal-body">
              {notesLead.Last_Next_Followup_Date ? (
                <p className="cportal-notes-modal-meta">
                  Next follow-up: {formatDate(notesLead.Last_Next_Followup_Date)}
                </p>
              ) : null}
              <div className="cportal-notes-modal-text">{notesLead.Last_Followup_Notes}</div>
            </div>
          </div>
        </div>
      )}

      {shareModal.open && (
        <div className="cportal-modal-overlay" onClick={closeShare}>
          <div className="cportal-modal lead-share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cportal-modal-header">
              <h3>Share Lead</h3>
              <button type="button" onClick={closeShare}>
                Close
              </button>
            </div>
            <div className="lead-share-modal-body">
              <p className="lead-share-title" title={shareModal.title}>
                {shareModal.title}
              </p>
              <div className="lead-share-preview">{shareModal.text}</div>
              <div className="lead-share-options">
                <a
                  className="lead-share-option whatsapp"
                  href={`https://wa.me/?text=${encodeURIComponent(shareModal.text)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeShare}
                >
                  <FaWhatsapp /> WhatsApp
                </a>
                <a
                  className="lead-share-option email"
                  href={`mailto:?subject=${encodeURIComponent(shareModal.title)}&body=${encodeURIComponent(shareModal.text)}`}
                  onClick={closeShare}
                >
                  <FaEnvelope /> Email
                </a>
                <button type="button" className="lead-share-option copy" onClick={copyShareText}>
                  <FaRegCopy /> Copy details
                </button>
                {typeof navigator !== "undefined" && navigator.share && (
                  <button type="button" className="lead-share-option more" onClick={nativeShare}>
                    <FaShareAlt /> More apps
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <MobileDialQrModal
        open={qrModal.open}
        title={qrModal.title}
        number={qrModal.number}
        dataUrl={qrModal.dataUrl}
        onClose={closeMobileQr}
      />

      <PortalConfirmModal
        open={!!deleteLead}
        title="Delete lead"
        message={
          deleteLead ? (
            <p>
              Delete <strong>{fullName(deleteLead)}</strong>? The lead will be removed from your
              list.
            </p>
          ) : null
        }
        confirming={!!deletingId}
        confirmingLabel="Deleting…"
        onConfirm={confirmDeleteLead}
        onClose={closeDeleteModal}
      />

      {followupLead ? (
        <PortalLeadQuickFollowupModal
          lead={followupLead}
          statuses={leadStatuses}
          onClose={closeFollowupModal}
          onSaved={handleFollowupSaved}
        />
      ) : null}

      <LeadImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(data) => {
          setStatusMessage({ type: "success", text: `${data.counts.imported} lead(s) imported.` });
          setReloadKey((k) => k + 1);
        }}
      />
    </div>
  );
}

export default TenantLeads;
